import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendTag } from '../../../projects/preview/components/trend-tag/TrendTag';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { getTrendValue, formatCount, formatPercent } from '../../../../shared/utils/utils';
import { RANGE_DAYS, RangeOption } from '../../hooks/useRangeFromUrl';

interface BotsActivityKpiTilesProps {
  totalVisits: number;
  visitsTrend: number | null;
  indexedUrls: number;
  indexedUrlsTrend: number | null;
  failureRate: number | null;
  previousFailureRate: number | null;
  activeRange: RangeOption;
}

const renderTrend = (trend: number | null, suffix = '%'): React.ReactNode => {
  if (trend === null) return null;
  return <TrendTag trendValue={getTrendValue(trend)} suffix={suffix} />;
};

const Tile: React.FC<{
  labelKey: string;
  tooltipKey: string;
  value: string;
  trend: React.ReactNode;
  daysLabel: string;
}> = ({ labelKey, tooltipKey, value, trend, daysLabel }) => {
  const { t } = useTranslation();
  return (
    <div className="bots-activity__hero-tile">
      <span className="bots-activity__hero-tile-label">
        {t(labelKey)}
        <CustomTooltip tooltipText={tooltipKey} addMargin={false} />
      </span>
      <span className="bots-activity__hero-tile-value-row">
        <span className="bots-activity__hero-tile-value">{value}</span>
        {trend}
      </span>
      <span className="bots-activity__hero-tile-subtitle">{daysLabel}</span>
    </div>
  );
};

export const BotsActivityKpiTiles: React.FC<BotsActivityKpiTilesProps> = ({
  totalVisits,
  visitsTrend,
  indexedUrls,
  indexedUrlsTrend,
  failureRate,
  previousFailureRate,
  activeRange,
}) => {
  const { t } = useTranslation();
  const daysLabel = t('botsActivity.vsLastDaysShort', { days: RANGE_DAYS[activeRange] });

  let failureTrend: React.ReactNode = null;
  if (failureRate != null && previousFailureRate != null && previousFailureRate > 0) {
    const deltaPp = Math.round((failureRate - previousFailureRate) * 100 * 10) / 10;
    failureTrend = <TrendTag trendValue={getTrendValue(deltaPp, 1)} suffix="pp" />;
  }

  return (
    <div className="bots-activity__hero-tile-row">
      <Tile
        labelKey="botsActivity.totalBotVisits"
        tooltipKey="botsActivity.totalBotVisitsHint"
        value={formatCount(totalVisits)}
        trend={renderTrend(visitsTrend, '%')}
        daysLabel={daysLabel}
      />
      <Tile
        labelKey="botsActivity.indexedUrls"
        tooltipKey="botsActivity.indexedUrlsHint"
        value={formatCount(indexedUrls)}
        trend={renderTrend(indexedUrlsTrend, '')}
        daysLabel={daysLabel}
      />
      <Tile
        labelKey="botsActivity.failureRate"
        tooltipKey="botsActivity.botRequestErrorsHint"
        value={formatPercent(failureRate)}
        trend={failureTrend}
        daysLabel={daysLabel}
      />
    </div>
  );
};

export default BotsActivityKpiTiles;
